import { JsonObj } from '@mhp/general-interface';
import { VisitSummary } from 'store/visitSummary/types';

export enum ItemTypes {
  ANNOUNCEMENT = 'announcements',
  HEALTH_ADVISORY = 'healthAdvisory',
  GOALS = 'goals',
  LINKED_ACCOUNT = 'linkedAccount',
  ANTICOAGULATION = 'anticoagulation',
  ERROR = 'error',
  NO_DATA = 'noData',
  ONBOARDING = 'onboarding',
  NEED_ACTIVATION = 'needActivation',
  PENDING_EVISITS = 'pending',
  COMPLETED_EVISITS = 'completed',
  RTA_SUMMARY = 'rtas'
}

export enum BookingTypes {
  CERVICAL_CANCER = 'CERVICAL CANCER SCREENING',
  DIABETES = 'Elevated HBA1C Monitoring',
  DIABETES_EYE = 'Diabetes Eye Exam',
  DIABETES_A1C = 'DIABETES HBA1C MONITORING',
  WELLNESS = 'ANNUAL VISIT',
  HPV_VACCINE = 'HPV VACCINE'
}

export enum AdvisoryType {
  CERVICAL_CANCER = 'CERVICAL_CANCER',
  DIABETES_A1C = 'DIABETES_A1C',
  FLU = 'FLU',
  HPV_VACCINE = 'HPV_VACCINE',
  MAMMOGRAM = 'MAMMOGRAM',
  UNKNOWN = 'UNKNOWN'
  // WELLNESS = 'WELLNESS'
}

export enum AdvisoryReferenceCDType {
  INFLUENZA_VACCINATION_CURRENT_SEASON = 'INFLUENZA VACCINATION CURRENT SEASON',
  PEDIATRIC_INFLUENZA_VACCINATION_CURRENT_SEASON = 'PEDIATRIC INFLUENZA VACCINATION CURRENT SEASON',
  FLU_VACCINE_9 = 'FLU_VACCINE_9',
  CANCER_SCREEN_CERVIX = 'CANCER_SCREEN_CERVIX',
  CERVICAL_CANCER_SCREENING = 'CERVICAL CANCER SCREENING',
  HUMAN_PAPILLOMAVIRUS_VACCINATION = 'HUMAN PAPILLOMAVIRUS VACCINATION',
  CANCER_SCREEN_BREAST_40_49 = 'CANCER_SCREEN_BREAST_40_49',
  MEDICARE_ANNUAL_VISIT = 'MEDICARE_ANNUAL_VISIT',
  CANCER_SCREEN_BREAST_50_75 = 'CANCER_SCREEN_BREAST_50_75',
  DIABETES_HBA1C_MONITORING = 'DIABETES_HBA1C_MONITORING',
  BREAST_CANCER_SCREENING_AGED_40_THROUGH_49 = 'BREAST CANCER SCREENING AGED 40 THROUGH 49',
  BREAST_CANCER_SCREENING = 'BREAST CANCER SCREENING',
  ELEVATED_HBA1C_MONITORING = 'ELEVATED HBA1C MONITORING'
}

export enum BookingVisitTypes {
  AWV = 'AWV',
  ROV = 'ROV'
}

export enum BookingVisitLabels {
  AWV = 'an annual wellness visit',
  ROV = 'a routine office visit'
}

export enum ClinicTypes {
  MAMMOGRAM = 'MAMMOGRAM',
  FLU_SHOT = 'INFLUENZA (FLU) VACCINE'
}

export interface BillingSummary {
  amountDue: number;
  dueStatus: string;
  totalEncounters: number;
}

export interface RTARelatedData {
  CLAIM_PLAN_NAME: string;
  FacilityID: string;
  FinNumber: string;
  MRN: string;
  claimInformation: ClaimInformation;
  claimReference: ClaimReference;
  claimStatus?: ClaimStatusEntity[] | null;
  controlNumber: string;
  externalReferenceNo: string;
  externalTransactionId: string;
  source: string;
  tradingPartnerId: string;
}

export interface ClaimInformation {
  accumulators?: AccumulatorsEntity[] | null;
  claimAcceptedDate: string;
  claimFromDate: string;
  claimLineInformation?: ClaimLineInformationEntity[] | null;
  claimToDate: string;
  customerClaimNumber: string;
  denialIndicator: string;
  memberInformation: MemberInformation;
  networkIndicator: string;
  paymentResponsibilityLevelCode: string;
  serviceProvider: ServiceProvider;
  totalChargeAmount: number;
  totalCoinsuranceAmount: number;
  totalCopaymentAmount: number;
  totalDeductibleAmount: number;
  totalEligibleAmount: number;
  totalNonCoveredAmount: number;
  totalPayableAmount: number;
}

export interface AccumulatorsEntity {
  accumulatorDescription: string;
  accumulatorValueFamily: number;
  accumulatorValueMember: number;
  accumulatorValueType: string;
}

export interface ClaimLineInformationEntity {
  capitatedLineItemIndicator: string;
  claimLine835?: ClaimLine835Entity[] | null;
  claimLineAllowAmount: number;
  claimLineChargeAmount: number;
  claimLineConsiderCharge: number;
  claimLineEOB: ClaimLineEOB;
  claimLineFromDate: string;
  claimLineMemberResp: number;
  claimLinePaidAmount: number;
  claimLineSequenceNo: number;
  claimLineToDate: string;
  claimLineUnits: number;
  claimLineUnitsAllow: number;
  clinicalEditFormatChangeIndicator: string;
  diagnosisCode: string;
  placeOfServiceCode: string;
  preAuthorizationIndicator: string;
  preAuthorizationNumber?: null;
  procedureCode: string;
  procedureCodeDescription: string;
}

export interface ClaimLine835Entity {
  claimLineAmount: number;
  claimLineCARC: string;
  claimLineGroupCode: string;
  claimLineRARC: string;
}

export interface ClaimLineEOB {
  claimLineCoinsuranceAmount: number;
  claimLineCopayAmount: number;
  claimLineDeductibleAmount: number;
  claimLineExplanationCodes?: ClaimLineExplanationCodesEntity[] | null;
  claimLineNonCoveredAmount: number;
}

export interface ClaimLineExplanationCodesEntity {
  claimLineDisallowExplanationCode?: string | null;
  claimLineDisallowExplanationDesc?: string | null;
}

export interface MemberInformation {
  medicaidNo: string;
  memberDOB: string;
  memberFirstName: string;
  memberLastName: string;
  memberRelation: string;
  subscriberDOB: string;
  subscriberFirstName: string;
  subscriberIdentifier: string;
  subscriberLastName: string;
}

export interface ServiceProvider {
  npi: string;
  providerLocationOnFile: ProviderLocationOnFile;
  providerName: string;
  providerType: string;
}

export interface ProviderLocationOnFile {
  address1: string;
  city: string;
  postalCode: string;
  state: string;
}

export interface ClaimReference {
  claimType: string;
  formatVersion: string;
  patientControlNumber: string;
  payerID: string;
  submitterId: string;
  timeOfResponse: string;
}

export interface ClaimStatusEntity {
  statusCategory: string;
  statusCode: string;
  statusEntity: string;
}

export interface RTASummary {
  id: string;
  eventTypeId: string;
  relatedData: RTARelatedData;
  sourceId: string;
  onAnalyticsClick: () => void;
  onAnalyticsDismissedClick: () => void;
}

export interface UpcomingAppointments {
  total: number;
  nextAppointmentDate: string;
}

export interface HealthAdvisory {
  eventId: string;
  surrogateUuid: string;
  advisoryName: string;
  advisoryType: AdvisoryType;
  advisoryReferenceCD?: AdvisoryReferenceCDType;
  snoozeOptions: {
    days: number[];
    nextSeason: boolean;
  };
}

export interface Goals {
  activeCount: number;
}

export interface Announcement {
  id: string;
  eventTypeId: string;
  relatedData: string;
  sourceId: string;
}

export interface ActionItems {
  healthAdvisories: HealthAdvisory[];
  goals: Goals;
  antiCoagulation: AntiCoagulationAlert;
  announcements: Announcement[];
}

export interface VisitTypes {
  data: JsonObj[];
  loading: boolean;
}

export interface HideEvent {
  loading: boolean;
  complete: boolean;
}

export interface AntiCoagulationAlert {
  eventId: string;
  sourceId: string;
  createdAt: string;
}

interface AnnouncementItem {
  type: ItemTypes.ANNOUNCEMENT;
  payload: Announcement;
}

interface RTASummaryItem {
  type: ItemTypes.RTA_SUMMARY;
  payload: RTASummary;
}

interface HealthAdvisoryItem {
  type: ItemTypes.HEALTH_ADVISORY;
  payload: HealthAdvisory;
}

interface GoalsItem {
  type: ItemTypes.GOALS;
  payload: Goals;
}

interface AntiCoagulationAlertItem {
  type: ItemTypes.ANTICOAGULATION;
  payload: AntiCoagulationAlert;
}

export interface ErrorItem {
  type: ItemTypes.ERROR;
  payload?: string[];
}

export interface NoDataItem {
  type: ItemTypes.NO_DATA;
}
export interface OnboardingItem {
  type: ItemTypes.ONBOARDING;
}

export interface EVisitsPendingStatus {
  type: ItemTypes.PENDING_EVISITS;
  payload: VisitSummary;
}
export interface EVisitsCompletedStatus {
  type: ItemTypes.COMPLETED_EVISITS;
  payload: VisitSummary;
}

export interface NeedActivation {
  type: ItemTypes.NEED_ACTIVATION;
  payload: JsonObj;
}

export type ProcessedActionItem =
  | AntiCoagulationAlertItem
  | AnnouncementItem
  | HealthAdvisoryItem
  | GoalsItem
  | ErrorItem
  | NoDataItem
  | OnboardingItem
  | EVisitsCompletedStatus
  | EVisitsPendingStatus
  | RTASummaryItem
  | NeedActivation;

export const QueryPathReadableName = {
  antiCoagulation: 'anti-coagulation',
  healthAdvisories: 'health advisories',
  announcements: 'announcements',
  goals: 'goals',
  upcomingAppointments: 'upcoming appointments',
  billingSummary: 'billing summary'
};

export type QueryPathReadableNameKey = keyof typeof QueryPathReadableName;

export interface ClearHealthAdvisoryFormValues {
  date: Date | null;
  location: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  providerName: string;
  phone: string;
  outCome: string;
  isTermsAccepted: boolean;
  providerGroupName: string;
}

export interface DashboardCardGraphQLErrorMessage {
  message: string;
  statusCode: number;
  error: string;
}

export interface DashboardCardGraphQLError {
  message: string | DashboardCardGraphQLErrorMessage;
  locations: object[];
  path: string[];
  extensions: object;
}

// the max amount of appointments to show on the dashboard before
// we call users to schedule an appointment, otherwise, they just
// view all appointments
export const MAX_APPOINTMENTS_THRESHOLD = 4;

export interface RTACardInfo {
  title: string;
  amount: string;
  date: string;
  buttonText: string;
}

export interface RTABasicInfo {
  provider: string;
  date: string;
  encounterId: string;
  patientId: string;
  patientName: string;
}

export interface RTABreakdown {
  totalPayableAmount: number;
  totalAdjustmentAmount: number;
  claimLineInformation: ClaimLineInformationEntity[];
  totalChargeAmount: number;
  CLAIM_PLAN_NAME: string;
  externalReferenceNo: string;
  totalEligibleAmount: number;
}

export interface RTAItemBody {
  left: string;
  right: string;
}
